import React, { useState, useMemo, useRef, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

const HISTORY_PAGE_SIZE = 5;

const SupplierLiveAuctionDashboard = ({
  liveBids = [],
  liveBidRanking = [],
  itemLeaderboard = [],
  auctionItems = [],
  auctionStatus = "active",
  closingDateTime = "",
  openingDateTime = "",
  auctionTitle = "",
  auctionId = "",
  auctionCurrency = "",
  autoExtensionMins = 2,
  displayRank = "showToAll",
  mbdType = "anyAmount",
  mbdValue = 0,
  fractions = "2",
  bidType = "perUnit",
  itemQuantities = {},
  itemMbd = {},
  itemReservePrice = {},
  currentUserCode = "",
  serverRemainingSeconds = null,
  emitPlaceBid = null,
  lastBidStatus = null,
  formInputs = {},
  supplierName = "",
}) => {
  //  Overall rank / bid from leaderboard 
  const myRankRow = useMemo(() => {
    return liveBidRanking.find(
      (r) => String(r.supplierCode) === String(currentUserCode)
    );
  }, [liveBidRanking, currentUserCode]);

  const myRank = myRankRow?.rank || "--";
  const myBid = myRankRow?.totalBid || 0;
  // eslint-disable-next-line
  const myLastBidTime = myRankRow?.lastBidTime || null;

  const lowestBid = liveBidRanking.length > 0 ? liveBidRanking[0].totalBid : 0;
  // eslint-disable-next-line
  const difference = myBid > 0 && lowestBid > 0 ? myBid - lowestBid : 0;

  //  Per-item rank lookup 
  const myItemRanks = useMemo(() => {
    const map = {};
    (itemLeaderboard || []).forEach((item) => {
      const myRow = (item.rankings || []).find(
        (r) => String(r.supplierCode) === String(currentUserCode)
      );
      const lowest = item.rankings?.length > 0 ? item.rankings[0] : null;
      map[item.itemKey || item.local_id || item.itemCode] = {
        myRank: myRow?.rank || null,
        myBid: myRow?.bidAmount || null,
        myTime: myRow?.bidTime || null,
        lowestBid: lowest?.bidAmount || null,
        lowestSupplier: lowest?.supplierName || "--",
        totalBidders: item.rankings?.length || 0,
      };
    });
    return map;
  }, [itemLeaderboard, currentUserCode]);

  //  My bids (filtered for this supplier) 
  const myBids = useMemo(() => {
    return liveBids
      .filter((b) => String(b.supplierCode) === String(currentUserCode))
      .sort((a, b) => new Date(b.bidTime || 0) - new Date(a.bidTime || 0));
  }, [liveBids, currentUserCode]);

  //  Bid history pagination 
  // eslint-disable-next-line
  const [historyPage, setHistoryPage] = useState(1);
  const visibleHistory = myBids.slice(0, historyPage * HISTORY_PAGE_SIZE);
  // eslint-disable-next-line
  const hasMoreHistory = myBids.length > visibleHistory.length;

  // ── Stat card reveal state 
  const [showItemModal, setShowItemModal] = useState(false);
  const [modalCard, setModalCard] = useState(null);
  const [pinned, setPinned] = useState(false);
  const hoverTimerRef = useRef(null);

  const openModal = useCallback((cardType) => {
    clearTimeout(hoverTimerRef.current);
    setModalCard(cardType);
    setShowItemModal(true);
  }, []);

  const scheduleClose = useCallback(() => {
    if (pinned) return;
    hoverTimerRef.current = setTimeout(() => {
      setShowItemModal(false);
    }, 200);
  }, [pinned]);

  const cancelClose = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
  }, []);

  // ── Helpers 
  const formatAmount = (amount) => {
    if (amount == null || amount === "") return "--";
    return Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
// eslint-disable-next-line
  const formatTime = (isoString) => {
    if (!isoString) return "--";
    try {
      return new Date(isoString).toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
    } catch { return isoString; }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "--";
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: true, timeZone: "Asia/Kolkata",
      });
    } catch { return dateStr; }
  };

  //  Rank visibility 
  // eslint-disable-next-line
  const canSeeRank = displayRank !== "notApplicable" &&
    (displayRank !== "onlyLeading" || myRank === 1);

  const isEnded = auctionStatus === "ended";
  const isActuallyEnded = isEnded && (serverRemainingSeconds === null || serverRemainingSeconds <= 0);

  const getRankLabel = (rank) => {
    if (rank === 1) return { label: "🥇 L1", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" };
    if (rank === 2) return { label: "🥈 L2", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
    if (rank === 3) return { label: "🥉 L3", color: "#7c2d12", bg: "#fff3ed", border: "#fb923c" };
    return { label: `🏅 L${rank}`, color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
  };
// eslint-disable-next-line
  const items = auctionItems.length > 0 ? auctionItems : (formInputs?.items || []);

  //  Item name lookup map (for bid history) 
  const itemNameMap = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const keys = [item.local_id, item.itemCode, item.itemNo, item.lotNumber, item.itemId].filter(Boolean);
      keys.forEach((key) => { map[key] = item; });
    });
    return map;
  }, [items]);

  // eslint-disable-next-line
  const getItemDisplayName = (bid) => {
    const code = bid.itemCode || bid.itemKey || bid.local_id || bid.lotNumber || bid.itemId || "";
    const item = itemNameMap[code];
    if (item) {
      const name = item.itemNo || item.itemCode || code;
      const desc = item.itemDescription || "";
      return desc ? `${name} — ${desc}` : name;
    }
    return code || "--";
  };

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* 
          TOP STATS CARDS — 4 equal cards
       */}
      <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
        {/*  Rank Card — masked until click  */}
        <Col xs={6} md={3}>
          <div
            style={{ ...statCardStyle, cursor: "pointer", position: "relative", overflow: "hidden", height: "100%" }}
            onClick={() => { setPinned(true); openModal("rank"); }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ ...statIconWrap, background: "#ecfdf5", color: "#059669" }}>
                <i className="bi bi-award-fill" style={{ fontSize: "16px" }}></i>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={statLabelStyle}>YOUR RANK</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#d1d5db" }}>--</div>
              </div>
            </div>
          </div>
        </Col>

        {/*  My Bid — masked until click  */}
        <Col xs={6} md={3}>
          <div
            style={{ ...statCardStyle, cursor: "pointer", position: "relative", overflow: "hidden", height: "100%" }}
            onClick={() => { setPinned(true); openModal("myBid"); }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ ...statIconWrap, background: "#f0fdfa", color: "#0f766e" }}>
                <i className="bi bi-currency-rupee" style={{ fontSize: "16px", fontWeight: 700 }}></i>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={statLabelStyle}>MY BID</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#d1d5db" }}>--</div>
              </div>
            </div>
          </div>
        </Col>

        {/* ── Lowest Bid — masked until click ── */}
        <Col xs={6} md={3}>
          <div
            style={{ ...statCardStyle, cursor: "pointer", position: "relative", overflow: "hidden", height: "100%" }}
            onClick={() => { setPinned(true); openModal("lowestBid"); }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ ...statIconWrap, background: "#eff6ff", color: "#2563eb" }}>
                <i className="bi bi-graph-down" style={{ fontSize: "16px" }}></i>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={statLabelStyle}>LOWEST BID</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#d1d5db" }}>--</div>
              </div>
            </div>
          </div>
        </Col>

        {/*  Gap to Lead — masked until click  */}
        <Col xs={6} md={3}>
          <div
            style={{ ...statCardStyle, cursor: "pointer", position: "relative", overflow: "hidden", height: "100%" }}
            onClick={() => { setPinned(true); openModal("gapToLead"); }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ ...statIconWrap, background: "#fff7ed", color: "#ea580c" }}>
                <i className="bi bi-arrow-down-right" style={{ fontSize: "16px" }}></i>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={statLabelStyle}>GAP TO LEAD</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#d1d5db" }}>--</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* 
          ITEM DETAIL MODAL — shows on stat card click
       */}
      {showItemModal && (
        <div
          onClick={() => { setShowItemModal(false); setPinned(false); }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            style={{
              background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "600px",
              maxHeight: "85vh", overflow: "auto", position: "relative",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            {/* Close button */}
            <button
              onClick={() => { setShowItemModal(false); setPinned(false); }}
              style={{
                position: "absolute", top: "12px", right: "12px",
                background: "#f1f5f9", border: "none", borderRadius: "50%",
                width: "32px", height: "32px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "16px", color: "#64748b", zIndex: 1,
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>

            {/* Header */}
            <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <i className="bi bi-list-check" style={{ color: "#0f766e", fontSize: "20px" }}></i>
                <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
                  {modalCard === "rank" && "Your Rank"}
                  {modalCard === "myBid" && "My Bids"}
                  {modalCard === "lowestBid" && "Lowest Bids"}
                  {modalCard === "gapToLead" && "Gap to Lead"}
                </span>
              </div>
            </div>

            {/* Item list */}
            <div style={{ padding: "16px 20px" }}>
              {items.map((item, idx) => {
                const itemKey = item.local_id || item.itemCode || idx;
                const info = myItemRanks[itemKey] || {};
                const rm = info.myRank ? getRankLabel(info.myRank) : null;
                const gap = info.myBid && info.lowestBid ? info.myBid - info.lowestBid : null;

                return (
                  <div key={itemKey} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px", borderRadius: "8px", border: "1px solid #e2e8f0",
                    marginBottom: idx < items.length - 1 ? "10px" : 0,
                    background: "#fafbfc",
                  }}>
                    {/* Item name */}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>
                        {item.itemNo || item.itemCode || `Item ${idx + 1}`}
                      </div>
                      {item.itemDescription && (
                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: "1px" }}>{item.itemDescription}</div>
                      )}
                    </div>

                    {/* Value — depends on which card was clicked */}
                    {modalCard === "rank" && (
                      <div>
                        {rm ? (
                          <span style={{
                            background: rm.bg, color: rm.color,
                            border: `1px solid ${rm.border}`, borderRadius: "6px",
                            padding: "4px 12px", fontWeight: 700, fontSize: "13px",
                          }}>{rm.label}</span>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: "13px" }}>No bid yet</span>
                        )}
                      </div>
                    )}

                    {modalCard === "myBid" && (
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>
                        {info.myBid ? `₹ ${formatAmount(info.myBid)}` : "--"}
                      </div>
                    )}

                    {modalCard === "lowestBid" && (
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>
                        {info.lowestBid ? `₹ ${formatAmount(info.lowestBid)}` : "--"}
                      </div>
                    )}

                    {modalCard === "gapToLead" && (
                      <div style={{ fontSize: "16px", fontWeight: 700, color: gap && gap > 0 ? "#ea580c" : "#0f172a", whiteSpace: "nowrap" }}>
                        {gap && gap > 0 ? `₹ ${formatAmount(gap)}` : info.myRank === 1 ? "Leading" : "--"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 
          MY BID HISTORY — moved into item detail modal
       */}

      {/* 
          AUCTION INFO | IMPORTANT NOTES — bottom of page
       */}
      <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
        <Col xs={12} md={6}>
          <div style={{ ...sectionCardStyle, padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
              <i className="bi bi-info-circle-fill" style={{ color: "#0f766e", fontSize: "16px" }}></i>
              <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>Auction Information</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { icon: "bi-tag-fill", label: "Auction Title", value: auctionTitle || formInputs?.auctionTitle || "--" },
                { icon: "bi-upc-scan", label: "Auction ID", value: auctionId || "--" },
                { icon: "bi-box-seam-fill", label: "Items", value: `${items.length} item(s)` },
                { icon: "bi-clock-fill", label: "Start Time", value: formatDateTime(openingDateTime) },
                { icon: "bi-clock-history", label: "End Time", value: formatDateTime(closingDateTime) },
                { icon: "bi-plus-circle-fill", label: "Auto Extension", value: `${autoExtensionMins || 2} minutes` },
                { icon: "bi-eye-fill", label: "Bid Visibility", value: "Rank & Lowest Bid" },
              ].map((item, idx) => (
                <div key={idx} style={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "8px 0", borderBottom: idx < 6 ? "1px solid #f1f5f9" : "none",
                }}>
                  <i className={`bi ${item.icon}`} style={{ color: "#0f766e", fontSize: "14px", width: "18px", textAlign: "center" }}></i>
                  <span style={{ fontSize: "13px", color: "#64748b", minWidth: "120px" }}>{item.label}</span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Col>

        <Col xs={12} md={6}>
          <div style={{ ...sectionCardStyle, padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
              <i className="bi bi-journal-text" style={{ color: "#0f766e", fontSize: "16px" }}></i>
              <span style={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>Important Notes</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {[
                { icon: "bi-patch-check-fill", iconColor: "#059669", iconBg: "#ecfdf5", text: "You are in a live reverse auction. Submit your best possible bid for each item." },
                { icon: "bi-clock-fill", iconColor: "#d97706", iconBg: "#fffbeb", text: `The auction will be automatically extended ${autoExtensionMins || 2} minutes before closing if a new bid is placed.` },
                { icon: "bi-info-circle-fill", iconColor: "#2563eb", iconBg: "#eff6ff", text: "Ensure you have reviewed all terms & conditions and item specifications before bidding." },
              ].map((note, idx) => (
                <div key={idx} style={{
                  display: "flex", alignItems: "flex-start", gap: "12px",
                  padding: "12px", borderRadius: "8px", background: note.iconBg, border: `1px solid ${note.iconColor}20`,
                }}>
                  <i className={`bi ${note.icon}`} style={{ color: note.iconColor, fontSize: "18px", marginTop: "2px", flexShrink: 0 }}></i>
                  <span style={{ fontSize: "13px", color: "#374151", lineHeight: 1.5 }}>{note.text}</span>
                </div>
              ))}
            </div>
            <div style={{
              marginTop: "20px", padding: "12px 16px", borderRadius: "8px",
              background: isActuallyEnded ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${isActuallyEnded ? "#fecaca" : "#a7f3d0"}`,
              display: "flex", alignItems: "center", gap: "8px",
            }}>
              <span style={{
                width: "8px", height: "8px", borderRadius: "50%",
                background: isActuallyEnded ? "#dc2626" : "#10b981",
                animation: !isActuallyEnded ? "pulse 1.5s infinite" : "none",
              }} />
              <span style={{ fontSize: "13px", fontWeight: 700, color: isActuallyEnded ? "#991b1b" : "#065f46" }}>
                {isActuallyEnded ? "Auction has ended" : auctionStatus === "extended" ? "Auction Extended" : "Auction is Live"}
              </span>
            </div>
          </div>
        </Col>
      </Row>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
};

const statCardStyle = {
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px",
  padding: "18px 16px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};

const statIconWrap = {
  width: "40px", height: "40px", borderRadius: "10px",
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};

const statLabelStyle = {
  fontSize: "10px", fontWeight: 600, color: "#94a3b8",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "2px",
};
// eslint-disable-next-line
const statSubStyle = { fontSize: "10px", color: "#94a3b8" };

const sectionCardStyle = {
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  display: "flex", flexDirection: "column",
};
// eslint-disable-next-line
const sectionHeaderStyle = {
  padding: "12px 16px", borderBottom: "1px solid #e2e8f0",
  display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fafbfc",
};
// eslint-disable-next-line
const thStyle = {
  padding: "10px 14px", textAlign: "left", color: "#64748b",
  fontWeight: 700, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.04em",
};
// eslint-disable-next-line
const tdStyle = { padding: "10px 14px", verticalAlign: "middle" };

export default SupplierLiveAuctionDashboard;
